import { Flow, Heading, Text } from 'bold-ui'
import { isProcedimentoSisab } from 'components/form/field/select/ProcedimentoSelectField/ProcedimentoSelectFieldItem'
import { LoadingIndicator } from 'components/loading'
import { useHistoricoCiapFiltroQuery, useHistoricoProcedimentoDwQuery } from 'graphql/hooks.generated'
import { Ciap, FatoProcedAtend } from 'graphql/types.generated'
import { partition } from 'lodash'
import { isEmpty } from 'util/checks'
import { HistoricoDwPanelProps } from 'view/atendimentos/detail/historico/model/model-historico'
import {
  convertFatoMedicoesToHistoricoMedicaoModel,
  hasMedicoes,
  mountInfosAdministrativas,
  naoRegistradoNesseAtendimento,
} from 'view/atendimentos/detail/historico/utils/utils-historico'

import HistoricoSection from '../../../../HistoricoSection'
import HistoricoDetailTable from '../../../../table/HistoricoDetailTable'
import { HistoricoDwPlanoSection } from '../../atendimento-individual/dw/components/HistoricoDwPlanoSection'
import { HistoricoDwSubjetivoSection } from '../../atendimento-individual/dw/components/HistoricoSubjetivoDwSection'
import { extractFromFiltro, isFiltroPreenchido } from '../../atendimento-individual/dw/utils-HistoricoDwCiapFiltro'
import HistoricoGapPanel from '../../HistoricoGapPanel'
import { HistoricoIvcfTableBox } from '../../HistoricoIvcfTableBox'
import HistoricoNoDetailAvailable from '../../HistoricoNoDetailAvailable'
import HistoricoMedicoesPanel from '../../medicoes/HistoricoMedicoesPanel'

export default function HistoricoProcedimentoDw({
  uuidRegistro,
  cpfOuCnsCidadao,
  idadeOrigemAtendimento,
}: Readonly<HistoricoDwPanelProps>) {
  const { data: { historicoProcedimentoDw: detalheProcedimentoDw } = {}, loading } = useHistoricoProcedimentoDwQuery({
    variables: {
      input: {
        uuidRegistro,
        cpfOuCnsCidadao,
        isOrigemPec: false,
      },
    },
  })

  const { data: { ciapsByCodigos: ciap2Filtro } = {}, loading: loadingCiap2Filtro } = useHistoricoCiapFiltroQuery({
    variables: {
      input: {
        codigos: [
          ...extractFromFiltro(detalheProcedimentoDw?.filtroCiapMotivoConsulta),
          ...extractFromFiltro(detalheProcedimentoDw?.filtroCiapPlano),
        ],
      },
    },
    skip:
      !detalheProcedimentoDw ||
      (!isFiltroPreenchido(detalheProcedimentoDw?.filtroCiapMotivoConsulta) &&
        !isFiltroPreenchido(detalheProcedimentoDw?.filtroCiapPlano)),
  })

  if (loading || loadingCiap2Filtro) return <LoadingIndicator />

  if (!detalheProcedimentoDw) return <HistoricoNoDetailAvailable />

  const [procedimentosSisab, procedimentosSigtap] = partition(detalheProcedimentoDw.procedimentos, (procedimento) =>
    isProcedimentoSisab(procedimento?.codigoProcedimento)
  )

  const medicoes = convertFatoMedicoesToHistoricoMedicaoModel(detalheProcedimentoDw as FatoProcedAtend)

  const hasAnyMedicao = hasMedicoes(medicoes)
  const hasIvcf = !isEmpty(detalheProcedimentoDw.ivcf)

  return (
    <HistoricoGapPanel
      idadeOrigemAtendimento={idadeOrigemAtendimento}
      informacoesAdministrativas={mountInfosAdministrativas(detalheProcedimentoDw)}
    >
      <Flow direction='vertical' gap={0.5}>
        <HistoricoDwSubjetivoSection ciap2Filtro={ciap2Filtro as Ciap[]} historico={detalheProcedimentoDw} />
        <HistoricoSection section='O'>
          <Flow direction='vertical' gap={0.5}>
            {!hasAnyMedicao && !hasIvcf && <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>}
            {hasAnyMedicao && <HistoricoMedicoesPanel sectionStyle='O' medicoes={medicoes} />}
            {hasIvcf && (
              <HistoricoIvcfTableBox
                ivcf={detalheProcedimentoDw.ivcf || undefined}
                riscoVulnerabilidadeText={
                  detalheProcedimentoDw?.ivcf?.dimensaoGrauVulnerabilidadeIvcf?.descricao as string
                }
              />
            )}
          </Flow>
        </HistoricoSection>
        <HistoricoDwPlanoSection
          ciap2Filtro={ciap2Filtro as Ciap[]}
          historico={detalheProcedimentoDw}
          procedimentosSisab={procedimentosSisab}
        />
        <HistoricoSection section='D'>
          {!isEmpty(procedimentosSigtap) ? (
            <Flow direction='vertical' gap={0.5}>
              <Heading level={5}>Procedimentos Administrativos (SIGTAP)</Heading>
              <HistoricoDetailTable hasLines>
                <Flow direction='vertical' gap={0}>
                  {procedimentosSigtap.map((procedimento) => (
                    <Text key={procedimento?.id}>
                      {procedimento?.descricaoProcedimento} - {procedimento?.codigoProcedimento}
                    </Text>
                  ))}
                </Flow>
              </HistoricoDetailTable>
            </Flow>
          ) : (
            <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
          )}
        </HistoricoSection>
      </Flow>
    </HistoricoGapPanel>
  )
}
